import type { Address, ChainId, MarketId } from '@morpho-org/blue-sdk'

import { encodeAbiParameters, encodePacked, getContractAddress, keccak256 } from 'viem'
import { base, mainnet } from 'viem/chains'

import { preLiquidationContractCreationCode } from './preLiquidationContractCreationCode'
import { preLiquidationContractCreationCodeLegacy } from './preLiquidationContractCreationCodeLegacy'

// Chains that use the legacy creation code (with IPFS hash in bytecode)
const CHAINS_WITH_LEGACY_CREATION_CODE = new Set<ChainId>([mainnet.id, base.id])

/**
 * Computes the deterministic address of a PreLiquidation contract using CREATE2.
 * Calculates where a PreLiquidation contract will be deployed based on factory address,
 * market parameters, and creation code using the CREATE2 opcode for deterministic deployment.
 * @param chainId - Chain ID to determine which creation code to use
 * @param morpho - Morpho protocol address
 * @param preLiquidationFactory - Factory contract address that will deploy PreLiquidation
 * @param marketId - Market identifier
 * @param preLiquidationParams - PreLiquidation configuration parameters (LLTV, close factors, incentive factors, oracle)
 * @returns Computed contract address
 */
export function computePreLiquidationAddress({
  chainId,
  morpho,
  preLiquidationFactory,
  marketId,
  preLiquidationParams
}: {
  chainId: ChainId
  morpho: Address
  preLiquidationFactory: Address
  marketId: MarketId
  preLiquidationParams: {
    preLltv: bigint
    preLCF1: bigint
    preLCF2: bigint
    preLIF1: bigint
    preLIF2: bigint
    preLiquidationOracle: Address
  }
}) {
  const creationCode = CHAINS_WITH_LEGACY_CREATION_CODE.has(chainId)
    ? preLiquidationContractCreationCodeLegacy
    : preLiquidationContractCreationCode

  const encodedParams = encodeAbiParameters(
    [
      { name: 'morpho', type: 'address' },
      { name: 'id', type: 'bytes32' },
      {
        name: 'preLiquidationParams',
        type: 'tuple',
        components: [
          { name: 'preLltv', type: 'uint256' },
          { name: 'preLCF1', type: 'uint256' },
          { name: 'preLCF2', type: 'uint256' },
          { name: 'preLIF1', type: 'uint256' },
          { name: 'preLIF2', type: 'uint256' },
          { name: 'preLiquidationOracle', type: 'address' }
        ]
      }
    ],
    [morpho, marketId, preLiquidationParams]
  )

  const initCodeHash = keccak256(encodePacked(['bytes', 'bytes'], [creationCode, encodedParams]))

  return getContractAddress({
    bytecodeHash: initCodeHash,
    from: preLiquidationFactory,
    salt: '0x0000000000000000000000000000000000000000000000000000000000000000',
    opcode: 'CREATE2'
  })
}
